import React, { useCallback, useRef } from 'react';
import { Formik, useFormikContext } from 'formik';
import { noop } from 'lodash';

import { useStyles } from '~/assets/styles';
import { Text } from '~/components/core';
import Link from '~/components/core/Atomic/Link/Link';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import LobMultiSelectWithChipsFormik from '~/components/core/Formik/LobMultiSelectWithChipsFormik';
import CancelIcon from '~/components/icons/CancelIcon';
import TextFieldFormik from '~/components/TextFieldFormik';
import Switch, { type SwitchChangeEvent } from '~/components/ui/atoms/Switch';
import cn from '~/Utils/cn';

export interface DocumentTypesFilterValues {
  search: string;
  lobs: (string | number)[];
  showOnlyCustomTypes: boolean;
  showOnlyEnabled: boolean;
}

export const DOCUMENT_TYPES_FILTER_INITIAL_VALUES: DocumentTypesFilterValues = {
  search: '',
  lobs: [],
  showOnlyCustomTypes: false,
  showOnlyEnabled: false,
};

const FILTER_FIELD_IDS = {
  SEARCH: 'search',
  LOBS: 'lobs',
  SHOW_ONLY_CUSTOM_TYPES: 'showOnlyCustomTypes',
  SHOW_ONLY_ENABLED: 'showOnlyEnabled',
} as const;

interface DocumentTypesFilterProps {
  onChange: (values: DocumentTypesFilterValues) => void;
}

interface DocumentTypesFilterFieldsProps {
  onChange: (values: DocumentTypesFilterValues) => void;
}

const DocumentTypesFilterFields: React.FC<DocumentTypesFilterFieldsProps> = ({ onChange }) => {
  const { classes } = useStyles();
  const { values, setFieldValue, resetForm } = useFormikContext<DocumentTypesFilterValues>();
  const suppressLobExpansionRef = useRef(false);

  useFormikChangeListener({
    onChange,
    listenForKeys: undefined,
    runOnFirstRender: false,
  });

  const handleShowOnlyCustomTypesChange = useCallback(
    (e: SwitchChangeEvent) => setFieldValue(FILTER_FIELD_IDS.SHOW_ONLY_CUSTOM_TYPES, e.target.checked),
    [setFieldValue]
  );

  const handleShowOnlyEnabledChange = useCallback(
    (e: SwitchChangeEvent) => setFieldValue(FILTER_FIELD_IDS.SHOW_ONLY_ENABLED, e.target.checked),
    [setFieldValue]
  );

  const handleClearAllFilters = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // Suppress the LOB unpick-All expansion that would otherwise refill
      // the lobs field with every individual LOB right after resetForm.
      suppressLobExpansionRef.current = true;
      resetForm();
    },
    [resetForm]
  );

  return (
    <div className="flex items-end">
      <div className="mb-2 mr-24 flex-1">
        <TextFieldFormik
          id={FILTER_FIELD_IDS.SEARCH}
          label="Search Document Types"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </div>
      <div className="mr-24 flex-1">
        <LobMultiSelectWithChipsFormik
          lobsFieldId={FILTER_FIELD_IDS.LOBS}
          suppressNextExpansionRef={suppressLobExpansionRef}
        />
      </div>
      <span className="mr-24 flex items-center">
        <Switch checked={values.showOnlyCustomTypes} onChange={handleShowOnlyCustomTypesChange} disabled={false} />{' '}
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
          Show only custom types
        </Text>
      </span>
      <span className="mr-24 flex items-center">
        <Switch checked={values.showOnlyEnabled} onChange={handleShowOnlyEnabledChange} disabled={false} />{' '}
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
          Show only enabled
        </Text>
      </span>
      <span className="flex h-[40px] items-center">
        <Link href="" onClick={handleClearAllFilters} className={cn(classes.secondaryLink, 'flex items-center')}>
          <CancelIcon className={cn(classes.filledIcon, classes.leftButtonIcon)} />
          Clear all filters
        </Link>
      </span>
    </div>
  );
};

const DocumentTypesFilter: React.FC<DocumentTypesFilterProps> = ({ onChange }) => {
  return (
    <CollapsibleWrapper title="Search Document Types" actionCard noBorder defaultState>
      <Formik<DocumentTypesFilterValues> initialValues={DOCUMENT_TYPES_FILTER_INITIAL_VALUES} onSubmit={noop}>
        <DocumentTypesFilterFields onChange={onChange} />
      </Formik>
    </CollapsibleWrapper>
  );
};

export default DocumentTypesFilter;
